/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
  Award, BarChart3, Download, Loader2, Sparkles, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const previousMonthValue = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUS_META = {
  performant:    { label: '🌟 Performant',     bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  a_suivre:      { label: '👀 À suivre',       bg: 'bg-amber-100',   text: 'text-amber-800',   border: 'border-amber-300' },
  a_accompagner: { label: '🤝 À accompagner',  bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300' },
  non_evalue:    { label: 'Non évalué',        bg: 'bg-zinc-100',    text: 'text-zinc-600',    border: 'border-zinc-300' },
};

const MODULE_LABEL = {
  discipline: 'Discipline',
  ventes: 'Score commercial',
  receptions: 'Réceptions',
  inventaires: 'Inventaires',
  diagnostics: 'Conv. diagnostics',
  patients: 'Fidélisation',
};

const scoreBar = (s) => {
  if (s == null) return 'bg-zinc-200';
  if (s >= 85) return 'bg-emerald-500';
  if (s >= 70) return 'bg-orange-500';
  if (s >= 60) return 'bg-amber-500';
  return 'bg-red-500';
};

const MyPerformanceCard = () => {
  const [period] = useState(previousMonthValue());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/agent-performance/report/me?period=${period}`, { headers: auth() });
        if (!cancelled) setReport(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Erreur chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  if (loading) {
    return (
      <Card className="border-zinc-200">
        <CardContent className="p-5 text-center text-sm text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Chargement de mes performances…
        </CardContent>
      </Card>
    );
  }

  if (error || !report) {
    return null; // discrétion : n'affiche rien si pas de données
  }

  if (report.excluded_from_ranking) {
    return (
      <Card className="border-zinc-200" data-testid="my-perf-empty">
        <CardContent className="p-5 text-sm text-zinc-600">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4 text-[#C45C26]" />
            <h3 className="font-semibold text-zinc-900">Mes performances · {report.period}</h3>
          </div>
          <p className="text-xs italic">Pas encore d'activité enregistrée pour ce mois — votre rapport sera disponible dès vos premiers actes.</p>
        </CardContent>
      </Card>
    );
  }

  const statusMeta = STATUS_META[report.status] || STATUS_META.non_evalue;
  const globalScore = report.global?.global_score;
  const modules = report.modules || {};
  const weights = report.global?.weights_applied || {};

  const exportMyPdf = async () => {
    try {
      const r = await axios.get(`${API}/agent-performance/report-pdf/me?period=${period}`, {
        headers: auth(), responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Mon_bilan_${period}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) { /* silent */ }
  };

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="my-perf-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-[#C45C26]" />
              <h3 className="font-serif font-semibold text-zinc-900">Mes performances · {report.period}</h3>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">
              Score global multi-modules · pondération adaptative
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-mono font-bold text-zinc-900 leading-none">{globalScore ?? '—'}</div>
            <div className="text-[10px] text-zinc-500 mt-1">/100</div>
            <Badge className={`${statusMeta.bg} ${statusMeta.text} ${statusMeta.border} border mt-1.5 text-[10px]`}>
              {statusMeta.label}
            </Badge>
            <button
              onClick={exportMyPdf}
              className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-900 hover:underline"
              data-testid="my-perf-pdf-btn"
            >
              <Download className="w-3 h-3" /> PDF
            </button>
          </div>
        </div>

        {report.improvement_message && (
          <div className="rounded-md bg-white border border-emerald-200 px-3 py-2 mb-3 text-sm text-zinc-700 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>{report.improvement_message}</span>
          </div>
        )}

        {/* Score par axe — barres compactes */}
        <div className="space-y-1.5">
          {Object.keys(MODULE_LABEL).map((k) => {
            const m = modules[k];
            const score = m?.score;
            const w = weights[k];
            return (
              <div key={k} className="flex items-center gap-2 text-xs" data-testid={`my-perf-axis-${k}`}>
                <span className="w-32 text-zinc-600 truncate">{MODULE_LABEL[k]}</span>
                <div className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${scoreBar(score)} transition-all`}
                    style={{ width: score == null ? '0%' : `${score}%` }}
                  />
                </div>
                <span className="w-12 text-right font-mono tabular-nums text-zinc-800">
                  {score == null ? '—' : `${score}`}
                </span>
                {w != null && (
                  <span className="w-10 text-right text-[10px] text-zinc-400 font-mono">{w}%</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Forces / faiblesses très condensées */}
        {(report.insights?.forces?.length > 0 || report.insights?.faiblesses?.length > 0) && (
          <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
            {report.insights?.forces?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="flex items-center gap-1 text-emerald-700 font-medium mb-1">
                  <TrendingUp className="w-3 h-3" /> Forces
                </div>
                <ul className="space-y-0.5 text-emerald-800">
                  {report.insights.forces.slice(0, 2).map((t, i) => <li key={i}>• {t}</li>)}
                </ul>
              </div>
            )}
            {report.insights?.faiblesses?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="flex items-center gap-1 text-amber-700 font-medium mb-1">À renforcer</div>
                <ul className="space-y-0.5 text-amber-800">
                  {report.insights.faiblesses.slice(0, 2).map((t, i) => <li key={i}>• {t}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyPerformanceCard;
